import FormCompanyImage from 'components/form/FormCompanyImage';
import FormRadio from 'components/form/FormRadio';
import FormSelect from 'components/form/FormSelect';
import FormText from 'components/form/FormText';
import { DivConst } from 'constants/divConst';

import { useEffect, type JSX } from 'react';
import { get, useFieldArray, useFormContext } from 'react-hook-form';
import type { FormEditFormStateType } from 'types/page/formEditFormStateType';
import { getDivSelectOptions } from 'utils/app';

/**
 * フォーム作成編集 設問設定コンポーネント
 */
export default function WsFormEditQuestionOptions(props: {
  questionIndex: number;
  readOnly: boolean;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  const {
    control,
    register,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useFormContext<FormEditFormStateType>();

  // 各入力タイプごとの選択肢配列を管理
  const optionsFieldArray = useFieldArray({
    control,
    name: `questions.${props.questionIndex}.options`,
  });

  // 登録可能最大件数を入力タイプごとに設定
  const getLimit = () => {
    const inputType = watch(`questions.${props.questionIndex}.questionType`);
    switch (inputType) {
      case DivConst.items.questionType.radio.value.toString():
        return DivConst.items.questionOptionLimit.radio;
      case DivConst.items.questionType.checkbox.value.toString():
        return DivConst.items.questionOptionLimit.checkbox;
      case DivConst.items.questionType.select.value.toString():
        return DivConst.items.questionOptionLimit.select;
      case DivConst.items.questionType.image.value.toString():
        return DivConst.items.questionOptionLimit.image;
      default:
        return DivConst.items.questionOptionLimit.image;
    }
  };

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  const watchedQuestionType = watch(`questions.${props.questionIndex}.questionType`);
  const watchedOptions = watch(`questions.${props.questionIndex}.options`);

  // 件数超過エラーの自動クリア
  useEffect(() => {
    const questionType = watchedQuestionType;
    const options = watchedOptions || [];
    const path = `questions.${props.questionIndex}.options` as const;

    const isRadio = questionType === DivConst.items.questionType.radio.value.toString();
    const isCheckbox = questionType === DivConst.items.questionType.checkbox.value.toString();
    const isImage = questionType === DivConst.items.questionType.image.value.toString();

    // 対象タイプで件数が上限以下、または対象タイプでない場合はエラーをクリア
    const radioLimit = DivConst.items.questionOptionLimit.radio;
    const checkboxLimit = DivConst.items.questionOptionLimit.checkbox;
    const imageLimit = DivConst.items.questionOptionLimit.image;
    if (
      (!isRadio && !isCheckbox && !isImage) ||
      (isRadio && options.length <= radioLimit) ||
      (isCheckbox && options.length <= checkboxLimit) ||
      (isImage && options.length <= imageLimit)
    ) {
      if (get(errors, path)) {
        clearErrors(path);
      }
    }
  }, [props.questionIndex, watchedQuestionType, watchedOptions, errors, clearErrors, watch]);

  // 設問タイプ変更時に選択肢が空の場合はデフォルトの選択肢を3つ追加
  useEffect(() => {
    const currentOptions = watchedOptions;
    if (currentOptions.length === 0) {
      setValue(`questions.${props.questionIndex}.options`, [
        { title: '', imageKey: null },
        { title: '', imageKey: null },
        { title: '', imageKey: null },
      ]);
    }
  }, [props.questionIndex, watchedOptions, setValue, watch]);
  // コンポーネントマウント時に各入力タイプの選択肢配列を初期化
  // useEffect(() => {
  //   const currentFieldArray = getCurrentFieldArray();
  //   const currentOptions = getCurrentOptions();

  //   // 各入力タイプの選択肢配列が空の場合は初期化
  //   if (!currentOptions || currentOptions.length === 0) {
  //     const defaultCount = 0;
  //     for (let i = 0; i < defaultCount; i++) {
  //       currentFieldArray.append({});
  //     }
  //   }
  // }, []);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * オプションの順番を入れ替えます。
   */
  const onMove = (optionIndex: number, upper: boolean) => {
    // const currentFieldArray = getCurrentFieldArray();
    // const { fields, move } = currentFieldArray;
    const { fields, move } = optionsFieldArray;

    // 先頭の場合は上に移動不可
    if (upper && optionIndex === 0) {
      return;
    }
    // 最終行の場合は下に移動不可
    if (!upper && optionIndex === fields.length - 1) {
      return;
    }
    // 要素の入れ替え
    move(optionIndex, upper ? optionIndex - 1 : optionIndex + 1);
  };

  /**
   * オプションを削除します。
   */
  const onRemove = (optionIndex: number) => {
    const { fields, remove } = optionsFieldArray;

    // ２件以下の場合は削除不可
    if (fields.length <= 2) {
      return;
    }

    // チェックボックスの場合、最大・最小チェック件数を調整
    const inputType = watch(`questions.${props.questionIndex}.questionType`);
    if (inputType === DivConst.items.questionType.checkbox.value.toString()) {
      const currentMaxCount = watch(`questions.${props.questionIndex}.checkboxMaxCount`);
      const currentMinCount = watch(`questions.${props.questionIndex}.checkboxMinCount`);
      const newOptionCount = fields.length - 1; // 削除後の選択肢数

      // 最大チェック件数が選択肢数を超える場合は調整
      if (Number(currentMaxCount) > newOptionCount) {
        setValue(`questions.${props.questionIndex}.checkboxMaxCount`, newOptionCount.toString());
      }

      // 最小チェック件数が選択肢数を超える場合は調整
      if (Number(currentMinCount) > newOptionCount) {
        setValue(`questions.${props.questionIndex}.checkboxMinCount`, newOptionCount.toString());
      }
    }

    // 要素の削除
    remove(optionIndex);
  };

  /**
   * オプションを追加します。
   */
  const onAppend = () => {
    const { append } = optionsFieldArray;

    // 上限以上の場合は追加不可
    if (optionsFieldArray.fields.length >= getLimit()) {
      return;
    }
    // 要素の追加
    append({});
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <div className="form-group form-group-md">
        {/* オプションタイプ選択 */}
        <fieldset className="mb-3 p-0">
          {/* <legend className="form-label">回答形式</legend> */}
          <FormRadio
            name={`questions.${props.questionIndex}.questionType`}
            register={register}
            errors={errors}
            direction="horizontal"
            readOnly={props.readOnly}
            options={getDivSelectOptions('questionType')}
          />
        </fieldset>

        {/* 文字選択時の文字数設定 */}
        {watch(`questions.${props.questionIndex}.questionType`) ==
          DivConst.items.questionType.text.value.toString() && (
          <div className="form-group mt-3">
            {/* 必須入力チェックボックス */}
            <div className="form-check mb-3">
              <input
                type="checkbox"
                {...register(`questions.${props.questionIndex}.requiredFlag`)}
                disabled={props.readOnly}
                id={`requiredText-${props.questionIndex}`}
                className="form-check-input"
              />
              <label className="form-check-label" htmlFor={`requiredText-${props.questionIndex}`}>
                必須入力
              </label>
            </div>
            <div className="d-flex align-items-center">
              <label className="form-label mb-0 me-2">文字数</label>
              <FormSelect
                name={`questions.${props.questionIndex}.textMaxLength`}
                register={register}
                errors={errors}
                disabled={props.readOnly}
                options={getDivSelectOptions('respondentCustomFieldMaxLength')}
              />
            </div>
            {/* プレースホルダー */}
            <div className="form-group mt-3">
              <label className="form-label">プレースホルダー</label>
              <FormText
                name={`questions.${props.questionIndex}.placeholder`}
                register={register}
                errors={errors}
                readOnly={props.readOnly}
                placeholder="プレースホルダー"
                className="w-100"
              />
            </div>
          </div>
        )}
        {/* 数字選択時の文字数設定 */}
        {watch(`questions.${props.questionIndex}.questionType`) ==
          DivConst.items.questionType.numeric.value.toString() && (
          <div className="form-group mt-3">
            {/* 必須入力チェックボックス */}
            <div className="form-check mb-3">
              <input
                type="checkbox"
                {...register(`questions.${props.questionIndex}.requiredFlag`)}
                disabled={props.readOnly}
                id={`requiredNumber-${props.questionIndex}`}
                className="form-check-input"
              />
              <label className="form-check-label" htmlFor={`requiredNumber-${props.questionIndex}`}>
                必須入力
              </label>
            </div>
            <div className="d-flex align-items-center">
              <label className="form-label mb-0 me-2">文字数</label>
              <FormSelect
                name={`questions.${props.questionIndex}.numberMaxLength`}
                register={register}
                errors={errors}
                disabled={props.readOnly}
                options={getDivSelectOptions('respondentCustomFieldMaxLength')}
              />
            </div>
          </div>
        )}

        {/* ラジオボタン選択時の選択肢設定 */}
        {watch(`questions.${props.questionIndex}.questionType`) ==
          DivConst.items.questionType.radio.value.toString() && (
          <div className="form-group form-group-md">
            {/* 必須入力チェックボックス */}
            <div className="form-check mb-3">
              <input
                type="checkbox"
                {...register(`questions.${props.questionIndex}.requiredFlag`)}
                disabled={props.readOnly}
                id={`requiredRadio-${props.questionIndex}`}
                className="form-check-input"
              />
              <label className="form-check-label" htmlFor={`requiredRadio-${props.questionIndex}`}>
                必須入力
              </label>
            </div>
            <div className="row">
              <div className="col">
                <label htmlFor="option1" className="form-label">
                  選択肢
                </label>
              </div>
            </div>
            {watch(`questions.${props.questionIndex}.options`).map((option, optionIndex) => {
              return (
                <div
                  key={`ws-form-edit-question[${props.questionIndex}]-radio-options-${optionIndex}`}
                >
                  <div className="form-group row align-items-center mb-2">
                    <div className="col-lg">
                      <div className="form-group-row">
                        <div className="col col-number">
                          <label htmlFor="option1" className="number">
                            {optionIndex + 1}
                          </label>
                        </div>
                        <div className="col">
                          <FormText
                            name={`questions.${props.questionIndex}.options.${optionIndex}.title`}
                            placeholder="選択肢を入力しよう"
                            register={register}
                            errors={errors}
                            readOnly={props.readOnly}
                            maxLength={100}
                            className="form-control-md"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg col-btn d-flex flex-grow-0 ms-auto text-nowrap">
                      <div className="btn-group">
                        <button
                          className="btn"
                          onClick={() => onMove(optionIndex, true)}
                          disabled={props.readOnly || optionIndex === 0}
                        >
                          <img src="/img/icon/arrow-up-black.svg" alt="" />
                        </button>
                        <button
                          className="btn"
                          onClick={() => onMove(optionIndex, false)}
                          disabled={
                            props.readOnly || optionIndex === optionsFieldArray.fields.length - 1
                          }
                        >
                          <img src="/img/icon/arrow-up-black.svg" alt="" className="scaley-1" />
                        </button>
                        <button
                          onClick={() => onRemove(optionIndex)}
                          disabled={props.readOnly || optionsFieldArray.fields.length <= 2}
                          className="btn"
                        >
                          <img src="/img/icon/trash.svg" alt="" className="filter-black" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {/* 選択肢の最大数超過時 */}
            {get(errors, `questions.${props.questionIndex}.options`) && (
              <p className="error">
                {get(errors, `questions.${props.questionIndex}.options`)?.type === 'custom' && (
                  <>{get(errors, `questions.${props.questionIndex}.options`)?.message}</>
                )}
              </p>
            )}
            <div className="btn-container max-width-238 mx-auto">
              <button
                className="btn btn-common moderate-blue w-100"
                onClick={onAppend}
                disabled={optionsFieldArray.fields.length >= getLimit() || props.readOnly}
              >
                + 選択肢を追加
              </button>
            </div>
          </div>
        )}

        {/* チェックボックス選択時の選択肢設定 */}
        {watch(`questions.${props.questionIndex}.questionType`) ==
          DivConst.items.questionType.checkbox.value.toString() && (
          <div className="form-group form-group-md">
            {/* チェック件数設定 */}
            <div className="form-group mt-3">
              <div className="d-flex align-items-center">
                <label className="form-label mb-0 me-2">最小チェック件数</label>
                <FormSelect
                  name={`questions.${props.questionIndex}.checkboxMinCount`}
                  register={register}
                  errors={errors}
                  disabled={props.readOnly}
                  options={(() => {
                    const optionCount = watch(`questions.${props.questionIndex}.options`).length;
                    return Array.from({ length: optionCount + 1 }, (_, i) => ({
                      value: i.toString(),
                      label: `${i}件`,
                    }));
                  })()}
                />
              </div>
            </div>
            <div className="form-group mt-3">
              <div className="d-flex align-items-center">
                <label className="form-label mb-0 me-2">最大チェック件数</label>
                <FormSelect
                  name={`questions.${props.questionIndex}.checkboxMaxCount`}
                  register={register}
                  errors={errors}
                  disabled={props.readOnly}
                  options={(() => {
                    const optionCount = watch(`questions.${props.questionIndex}.options`).length;
                    return Array.from({ length: optionCount + 1 }, (_, i) => ({
                      value: i.toString(),
                      label: `${i}件`,
                    }));
                  })()}
                />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <label htmlFor="option1" className="form-label">
                  選択肢
                </label>
              </div>
            </div>
            {watch(`questions.${props.questionIndex}.options`).map((option, optionIndex) => {
              return (
                <div
                  key={`ws-form-edit-question[${props.questionIndex}]-checkbox-options-${optionIndex}`}
                >
                  <div className="form-group row align-items-center mb-2">
                    <div className="col-lg">
                      <div className="form-group-row">
                        <div className="col col-number">
                          <label htmlFor="option1" className="number">
                            {optionIndex + 1}
                          </label>
                        </div>
                        <div className="col">
                          <FormText
                            name={`questions.${props.questionIndex}.options.${optionIndex}.title`}
                            placeholder="選択肢を入力しよう"
                            register={register}
                            errors={errors}
                            readOnly={props.readOnly}
                            maxLength={100}
                            className="form-control-md"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg col-btn d-flex flex-grow-0 ms-auto text-nowrap">
                      <div className="btn-group">
                        <button
                          className="btn"
                          onClick={() => onMove(optionIndex, true)}
                          disabled={props.readOnly || optionIndex === 0}
                        >
                          <img src="/img/icon/arrow-up-black.svg" alt="" />
                        </button>
                        <button
                          className="btn"
                          onClick={() => onMove(optionIndex, false)}
                          disabled={
                            props.readOnly || optionIndex === optionsFieldArray.fields.length - 1
                          }
                        >
                          <img src="/img/icon/arrow-up-black.svg" alt="" className="scaley-1" />
                        </button>
                        <button
                          onClick={() => onRemove(optionIndex)}
                          disabled={props.readOnly || optionsFieldArray.fields.length <= 2}
                          className="btn"
                        >
                          <img src="/img/icon/trash.svg" alt="" className="filter-black" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {/* 選択肢の最大数超過時 */}
            {get(errors, `questions.${props.questionIndex}.options`) && (
              <p className="error">
                {get(errors, `questions.${props.questionIndex}.options`)?.type === 'custom' && (
                  <>{get(errors, `questions.${props.questionIndex}.options`)?.message}</>
                )}
              </p>
            )}
            <div className="btn-container max-width-238 mx-auto">
              <button
                className="btn btn-common moderate-blue w-100"
                onClick={onAppend}
                disabled={optionsFieldArray.fields.length >= getLimit() || props.readOnly}
              >
                + 選択肢を追加
              </button>
            </div>
          </div>
        )}

        {/* セレクトボックス選択時の選択肢設定 */}
        {watch(`questions.${props.questionIndex}.questionType`) ==
          DivConst.items.questionType.select.value.toString() && (
          <div className="form-group form-group-md">
            {/* 必須入力チェックボックス */}
            <div className="form-check mb-3">
              <input
                type="checkbox"
                {...register(`questions.${props.questionIndex}.requiredFlag`)}
                disabled={props.readOnly}
                id={`requiredSelect-${props.questionIndex}`}
                className="form-check-input"
              />
              <label className="form-check-label" htmlFor={`requiredSelect-${props.questionIndex}`}>
                必須入力
              </label>
            </div>
            <div className="row">
              <div className="col">
                <label htmlFor="option1" className="form-label">
                  選択肢
                </label>
              </div>
            </div>
            {watch(`questions.${props.questionIndex}.options`).map((option, optionIndex) => {
              return (
                <div
                  key={`ws-form-edit-question[${props.questionIndex}]-select-options-${optionIndex}`}
                >
                  <div className="form-group row align-items-center mb-2">
                    <div className="col-lg">
                      <div className="form-group-row">
                        <div className="col col-number">
                          <label htmlFor="option1" className="number">
                            {optionIndex + 1}
                          </label>
                        </div>
                        <div className="col">
                          <FormText
                            name={`questions.${props.questionIndex}.options.${optionIndex}.title`}
                            placeholder="選択肢を入力しよう"
                            register={register}
                            errors={errors}
                            readOnly={props.readOnly}
                            maxLength={100}
                            className="form-control-md"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg col-btn d-flex flex-grow-0 ms-auto text-nowrap">
                      <div className="btn-group">
                        <button
                          className="btn"
                          onClick={() => onMove(optionIndex, true)}
                          disabled={props.readOnly || optionIndex === 0}
                        >
                          <img src="/img/icon/arrow-up-black.svg" alt="" />
                        </button>
                        <button
                          className="btn"
                          onClick={() => onMove(optionIndex, false)}
                          disabled={
                            props.readOnly || optionIndex === optionsFieldArray.fields.length - 1
                          }
                        >
                          <img src="/img/icon/arrow-up-black.svg" alt="" className="scaley-1" />
                        </button>
                        <button
                          onClick={() => onRemove(optionIndex)}
                          disabled={props.readOnly || optionsFieldArray.fields.length <= 2}
                          className="btn"
                        >
                          <img src="/img/icon/trash.svg" alt="" className="filter-black" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="btn-container max-width-238 mx-auto">
              <button
                className="btn btn-common moderate-blue w-100"
                onClick={onAppend}
                disabled={optionsFieldArray.fields.length >= getLimit() || props.readOnly}
              >
                + 選択肢を追加
              </button>
            </div>
          </div>
        )}

        {/* 画像のボタン選択時の選択肢設定 */}
        {watch(`questions.${props.questionIndex}.questionType`) ==
          DivConst.items.questionType.image.value.toString() && (
          <div className="form-group form-group-md">
            {/* 必須入力チェックボックス */}
            <div className="form-check mb-3">
              <input
                type="checkbox"
                {...register(`questions.${props.questionIndex}.requiredFlag`)}
                disabled={props.readOnly}
                id={`requiredImage-${props.questionIndex}`}
                className="form-check-input"
              />
              <label className="form-check-label" htmlFor={`requiredImage-${props.questionIndex}`}>
                必須入力
              </label>
            </div>
            <div className="row">
              <div className="col">
                <label htmlFor="option1" className="form-label">
                  選択肢
                </label>
              </div>
            </div>
            {watch(`questions.${props.questionIndex}.options`).map((option, optionIndex) => {
              return (
                <div
                  key={`ws-form-edit-question[${props.questionIndex}]-image-options-${optionIndex}`}
                >
                  <div className="form-group row align-items-center mb-2">
                    <div className="col-lg">
                      <div className="form-group-row">
                        <div className="col col-number">
                          <label htmlFor="option1" className="number">
                            {optionIndex + 1}
                          </label>
                        </div>
                        <div className="col">
                          <FormText
                            name={`questions.${props.questionIndex}.options.${optionIndex}.title`}
                            placeholder="選択肢を入力しよう"
                            register={register}
                            errors={errors}
                            readOnly={props.readOnly}
                            maxLength={100}
                            className="form-control-md"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg col-btn d-flex flex-grow-0 ms-auto text-nowrap">
                      <div className="btn-group">
                        <button
                          className="btn"
                          onClick={() => onMove(optionIndex, true)}
                          disabled={props.readOnly || optionIndex === 0}
                        >
                          <img src="/img/icon/arrow-up-black.svg" alt="" />
                        </button>
                        <button
                          className="btn"
                          onClick={() => onMove(optionIndex, false)}
                          disabled={
                            props.readOnly || optionIndex === optionsFieldArray.fields.length - 1
                          }
                        >
                          <img src="/img/icon/arrow-up-black.svg" alt="" className="scaley-1" />
                        </button>
                        <button
                          onClick={() => onRemove(optionIndex)}
                          disabled={props.readOnly || optionsFieldArray.fields.length <= 2}
                          className="btn"
                        >
                          <img src="/img/icon/trash.svg" alt="" className="filter-black" />
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* 画像アップロード */}
                  <div className="form-group row align-items-center mb-2">
                    <div className="col-lg">
                      <div className="form-group-row">
                        <div className="col col-number">{/* 空のスペース（番号と同じ位置） */}</div>
                        <div className="col">
                          <div className="card form-header">
                            <h4 className="title">選択肢{optionIndex + 1}の画像</h4>
                            <FormCompanyImage
                              control={control}
                              name={`questions.${props.questionIndex}.options.${optionIndex}.imageKey`}
                              readOnly={props.readOnly}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg col-btn d-flex flex-grow-0 ms-auto text-nowrap">
                      {/* 空のスペース（ボタンと同じ位置） */}
                    </div>
                  </div>
                </div>
              );
            })}
            {/* 選択肢の最大数超過時 */}
            {get(errors, `questions.${props.questionIndex}.options`) && (
              <p className="error">
                {get(errors, `questions.${props.questionIndex}.options`)?.type === 'custom' && (
                  <>{get(errors, `questions.${props.questionIndex}.options`)?.message}</>
                )}
              </p>
            )}
            <div className="btn-container max-width-238 mx-auto">
              <button
                className="btn btn-common moderate-blue w-100"
                onClick={onAppend}
                disabled={optionsFieldArray.fields.length >= getLimit() || props.readOnly}
              >
                + 選択肢を追加
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
